import React, { useState, useRef, useLayoutEffect } from "react";
import { makeStyles } from "@material-ui/core";

var useStyles = makeStyles(function () {
  return {
    "@keyframes fadeInUp": {
      from: { opacity: 0, transform: "translateY(6px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    panel: {
      border: "1px solid #E2E5EA",
      borderRadius: "10px",
      overflow: "hidden",
      marginTop: "8px",
      animation: "$fadeInUp 200ms ease-out both",
    },
    header: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "11px 16px",
      background: "#F8F9FB",
      borderBottom: function (props) {
        return props.isOpen ? "1px solid #E2E5EA" : "1px solid transparent";
      },
      cursor: "pointer",
      userSelect: "none",
      transition: "background 150ms",
      "&:hover": {
        background: "#F3F4F6",
      },
    },
    headerIcon: {
      width: "18px",
      height: "18px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, #F59E0B, #D97706)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      color: "#fff",
      fontSize: "11px",
      fontWeight: 700,
      lineHeight: 1,
    },
    headerTitle: {
      fontFamily: "'DM Sans', sans-serif",
      fontSize: "13px",
      fontWeight: 600,
      color: "#374151",
    },
    headerCount: {
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: "11px",
      fontWeight: 500,
      color: "#6B7280",
      background: "#EDEEF1",
      borderRadius: "4px",
      padding: "1px 7px",
    },
    headerChevron: {
      marginLeft: "auto",
      color: "#9CA3AF",
      fontSize: "12px",
      transition: "transform 200ms ease-out",
      lineHeight: 1,
    },
    headerChevronClosed: {
      transform: "rotate(-90deg)",
    },
    body: {
      transition: "height 250ms ease-out, opacity 200ms ease-out",
      overflow: "hidden",
    },
    item: {
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
      padding: "11px 16px",
      borderBottom: "1px solid #F0F1F3",
      transition: "background 100ms",
      "&:last-child": {
        borderBottom: "none",
      },
      "&:hover": {
        background: "#FAFBFC",
      },
    },
    sevBadge: {
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: "10px",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      padding: "2px 7px",
      borderRadius: "4px",
      marginTop: "1px",
      flexShrink: 0,
      lineHeight: 1.5,
    },
    sevHigh: {
      background: "#FEF3C7",
      color: "#92400E",
    },
    sevMedium: {
      background: "#F3F4F6",
      color: "#4B5563",
    },
    sevLow: {
      background: "#DBEAFE",
      color: "#1E40AF",
    },
    itemContent: {
      flex: 1,
      minWidth: 0,
    },
    itemDesc: {
      fontFamily: "'IBM Plex Sans', sans-serif",
      fontSize: "13.5px",
      lineHeight: 1.45,
      color: "#374151",
    },
    itemMetric: {
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: "10.5px",
      color: "#9CA3AF",
      marginTop: "2px",
    },
    itemPct: {
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: "12px",
      fontWeight: 600,
      flexShrink: 0,
      marginTop: "1px",
    },
    itemPctHigh: {
      color: "#DC2626",
    },
    itemPctMedium: {
      color: "#6B7280",
    },
  };
});

function extractPercentage(text) {
  if (!text) return null;
  var match = text.match(/(-?\d+(?:\.\d+)?%)/);
  return match ? match[1] : null;
}

var RiskFlags = React.memo(function RiskFlags(_ref) {
  var flags = _ref.flags;
  var _open = useState(true);
  var isOpen = _open[0];
  var setIsOpen = _open[1];
  var _height = useState("auto");
  var measuredHeight = _height[0];
  var setMeasuredHeight = _height[1];
  var bodyRef = useRef(null);

  var classes = useStyles({ isOpen: isOpen });

  useLayoutEffect(function () {
    if (bodyRef.current) {
      setMeasuredHeight(bodyRef.current.scrollHeight + "px");
    }
  }, [flags]);

  if (!flags || !flags.length) return null;

  function getSevClass(severity) {
    if (severity === "high") return classes.sevHigh;
    if (severity === "low") return classes.sevLow;
    return classes.sevMedium;
  }

  function getSevLabel(severity) {
    if (severity === "high") return "HIGH";
    if (severity === "low") return "LOW";
    return "MED";
  }

  function getPctClass(severity) {
    if (severity === "high") return classes.itemPctHigh;
    return classes.itemPctMedium;
  }

  function handleToggle() {
    setIsOpen(!isOpen);
  }

  return (
    <div className={classes.panel}>
      <div className={classes.header} onClick={handleToggle}>
        <div className={classes.headerIcon}>!</div>
        <span className={classes.headerTitle}>Risk Findings</span>
        <span className={classes.headerCount}>{flags.length}</span>
        <span
          className={
            classes.headerChevron +
            (isOpen ? "" : " " + classes.headerChevronClosed)
          }
        >
          &#9662;
        </span>
      </div>
      <div
        ref={bodyRef}
        className={classes.body}
        style={{
          height: isOpen ? measuredHeight : "0px",
          opacity: isOpen ? 1 : 0,
        }}
      >
        {flags.map(function (f, i) {
          var pct = extractPercentage(f.description);
          return (
            <div key={i} className={classes.item}>
              <span
                className={classes.sevBadge + " " + getSevClass(f.severity)}
              >
                {getSevLabel(f.severity)}
              </span>
              <div className={classes.itemContent}>
                <div className={classes.itemDesc}>{f.description}</div>
                {f.metric && (
                  <div className={classes.itemMetric}>{f.metric}</div>
                )}
              </div>
              {pct && (
                <span
                  className={
                    classes.itemPct + " " + getPctClass(f.severity)
                  }
                >
                  {pct}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default RiskFlags;
